/**
 * Screen-level UI strings (forms, tables, filters) — EN / AR.
 * Use: const ui = useMemo(() => getAppUi(language), [language]); ui('common.save');
 */

function resolveDot(root, dotPath) {
  if (!root || !dotPath) return undefined;
  let cur = root;
  for (const p of dotPath.split('.')) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

const UI = {
  en: {
    common: {
      save: 'Save',
      saveAndPrint: 'Save & Print',
      view: 'View',
      refresh: 'Refresh',
      print: 'Print',
      delete: 'Delete',
      previous: 'Previous',
      next: 'Next',
      search: 'Search',
      cancel: 'Cancel',
      confirm: 'Confirm',
      close: 'Close',
      select: 'Select',
      viewDetails: 'View details',
      viewOrderList: 'View Order List',
      status: 'Status',
      apply: 'Apply',
      clear: 'Clear',
      filters: 'Filters',
      exportExcel: 'Export Excel',
      edit: 'Edit',
      previousPage: 'Previous page',
      nextPage: 'Next page',
    },
    columns: {
      sNo: 'S.No',
      voucherNo: 'Voucher No',
      clientBillNo: 'Client Bill No',
      batchNo: 'Batch No',
      client: 'Client',
      customer: 'Customer',
      deliveryAddress: 'Delivery Address',
      orderDate: 'Order Date',
      deliveryDate: 'Delivery Date',
      product: 'Product',
      deliveryCh: 'Delivery Ch.',
      total: 'Total',
      payType: 'Pay Type',
      action: 'Action',
      mobile: 'Mobile',
      emirate: 'Emirate',
      area: 'Area',
      refNo: 'Ref No',
      date: 'Date',
      driver: 'Driver',
      vehicle: 'Vehicle',
      remarks: 'Remarks',
      createdBy: 'Created By',
      employee: 'Employee',
      clients: 'Clients',
      totalOrders: 'Total Orders',
      options: 'Options',
      option: 'Option',
      amount: 'Amount',
      deliveryNote: 'Delivery Note',
      expectedAmount: 'Expected Amount',
      totalExpense: 'Total Expense',
      cashInHand: 'Cash In Hand',
      totalIncome: 'Total Income',
      totalVat: 'Total VAT',
      clientAddress: 'Client Address',
      customerAddress: 'Customer Address',
      productPrice: 'Product Price',
      deliveryCharge: 'Delivery Charge',
      tax: 'TAX',
      customerAmount: 'Customer Amount',
      clientAmount: 'Client Amount',
      clientMob: 'Client Mob',
      clientEmirate: 'Client Emirate',
      clientLocation: 'Client Location',
      customerMob: 'Customer Mob',
      customerEmirate: 'Customer Emirate',
      customerLocation: 'Customer Location',
      clientBill: 'Client Bill',
      totalAdvance: 'Total Advance',
      balanceIncome: 'Balance Income',
      totalMoneyFromClient: 'Total Money From Client',
      totalClientMoney: 'Total Client Money',
      createdDate: 'Created Date',
    },
    orders: {
      orderList: {
        keyword: 'Keyword',
        keywordPlaceholder: 'Client / Customer / Bill No / Mobile',
        voucherPlaceholder: 'Voucher No',
        noOrders: 'No orders found.',
        showingToOf: 'Showing {from} to {to} of {total} orders',
        pageOf: 'Page {page} of {totalPages}',
      },
      todaysOrders: {
        statTotalOrders: 'Total Orders',
        statDelivered: 'Delivered',
        statPending: 'Pending',
        statInTransit: 'In Transit',
        statReturn: 'Return',
        statCancelled: 'Cancelled',
        byEmirate: 'By Emirate',
        byArea: 'By Area',
        byStatus: 'By Status',
        noData: 'No data',
        orderListHeading: 'Order List',
        noOrdersToday: 'No orders for today.',
        showingToOf: 'Showing {from} to {to} of {total}',
        pageOf: 'Page {page} of {totalPages}',
      },
      createOrder: {
        statusPrefix: 'Status:',
        voucherNo: 'Voucher No',
        refNo: 'Ref No',
        pickUpBy: 'Pick Up By',
        payType: 'Pay Type',
        chargeFrom: 'Charge From',
        clientBillNo: 'Client Bill No',
        type: 'Type',
        agent: 'Agent',
        clearSelection: 'Clear selection',
        searchSelectDriver: 'Search or select driver',
        paymentTypeDetails: 'Payment Type Details',
        selectType: 'Select Type',
        selectAgent: 'Select Agent',
        chargeFromClient: 'Client',
        chargeFromArea: 'Area',
        forPickup: 'For Pickup?',
        exchange: 'Exchange?',
        fragile: 'Fragile?',
        deliveringArea: 'Delivering Area:',
        sectionClient: 'Client',
        sectionCustomer: 'Customer',
        clientName: 'Client Name',
        customerName: 'Customer Name',
        clientNo: 'Client No',
        mobileNo: 'Mobile No',
        mobileNo1: 'Mobile No 1',
        mobileNo2: 'Mobile No 2',
        emirate: 'Emirate',
        location: 'Location',
        locationUrl: 'Location URL',
        address: 'Address',
        poBox: 'PO Box',
        phSearchNewClient: 'Search or type new client',
        phTypeClientNumber: 'Type client number',
        phTypeMobile: 'Type mobile number',
        phSearchNewCustomer: 'Search or type new customer',
        phOptional: 'Optional',
        selectEmirate: 'Select Emirate',
        phSearchLocation: 'Search or select location',
        phUrl: 'URL',
        phEnterAddress: 'Enter address',
        phPoBox: 'PO Box',
        phRemarks: 'Enter remarks or notes',
        phAmount: '0.00',
        refreshClient: 'Refresh Client',
        refreshAddress: 'Refresh Address',
        searchAddress: 'Search Address',
        refreshCustomer: 'Refresh Customer',
        editCustomer: 'Edit Customer',
        orderDetailsFinancial: 'Order Details & Financial',
        remarks: 'Remarks',
        quantity: 'Quantity',
        pickupDate: 'Pickup Date',
        deliveryDate: 'Delivery Date',
        productPrice: 'Product Price',
        deliveryCharge: 'Delivery Charge',
        tax: 'Tax',
        clientAmount: 'Client Amount',
        customerAmount: 'Customer Amount',
        confirmDeleteTitle: 'Confirm Delete',
        confirmDeleteBody: 'Click Confirm to delete this order.',
        clientAddressTitle: 'Client Address',
        customerAddressTitle: 'Customer Address',
        addressTablePoBox: 'PO BOX',
        paymentTableType: 'Type',
        paymentTableRemarks: 'Remarks',
        payCad: 'Delivery charge after delivery',
        payCod: 'Delivery charge at delivery',
        payCao: 'Advance delivery charge received at office as cash',
        payTao: 'Advance delivery charge received as online transfer',
        payFoc: 'Free delivery charge',
        payAps: 'Product price paid advance, Client will pay delivery charge',
        payApr: 'Product price paid advance, Customer will pay delivery charge',
      },
    },
    delivery: {
      notes: {
        subtitle: 'Manage and view all delivery notes',
        listTitle: 'Delivery Notes List',
        totalCount: 'Total: {total} delivery notes',
        searchRefLabel: 'Search (Ref No)',
        searchRefPlaceholder: 'Search by reference number...',
        allDrivers: 'All Drivers',
        allStatus: 'All Status',
        statusConfirmed: 'Confirmed',
        statusNew: 'New',
        statusFinished: 'Finished',
        loading: 'Loading delivery notes...',
        noNotes: 'No delivery notes found',
        noNotesHint: 'Try adjusting your filters or refresh the page',
        ordersInNote: 'Orders ({count})',
        detailsTitle: 'Delivery note details',
        showingToOf: 'Showing {from} to {to} of {total} delivery notes',
        confirmDeleteTitle: 'Confirm delete',
        confirmDeleteMessage:
          'Are you sure you want to delete delivery note "{refNo}"?',
        confirmDeleteWarning:
          'This action cannot be undone. All associated data will be permanently deleted.',
        deleting: 'Deleting…',
        printError: 'Print error',
      },
    },
    shipments: {
      list: {
        subtitle: 'View and manage all shipments',
        searchPlaceholder: 'Search by Ref No...',
        typeLabel: 'Type',
        typeAll: 'All',
        typeNew: 'New (unapproved)',
        loading: 'Loading shipments...',
        loadingMore: 'Loading more shipments...',
        noShipments: 'No shipments found',
        showingToOf: 'Showing {from} to {to} of {total} shipments',
        showingLoadedOf: 'Showing {loaded} of {total} shipments',
        scrollToLoadMore: '(Scroll down to load more)',
        loadFailed: 'Failed to load shipments',
        errorGeneric: 'Something went wrong',
        noDataExport: 'No data to export',
        exportSuccess: 'Excel file exported successfully',
        exportFailed: 'Failed to export Excel file',
        printFailed: 'Failed to print shipment',
        deleteSuccess: 'Shipment deleted successfully',
        deleteFailed: 'Failed to delete shipment',
        confirmDeleteTitle: 'Confirm delete',
        confirmDeleteMessage: 'Are you sure you want to delete shipment "{refNo}"?',
        confirmDeleteWarning: 'Only unapproved shipments can be deleted. The delivery note will be moved back to confirmed.',
        deleting: 'Deleting...',
        statusApproved: 'Approved',
        statusPending: 'Pending',
        sheetName: 'Shipments',
      },
    },
  },
  ar: {
    common: {
      save: 'حفظ',
      saveAndPrint: 'حفظ وطباعة',
      view: 'عرض',
      refresh: 'تحديث',
      print: 'طباعة',
      delete: 'حذف',
      previous: 'السابق',
      next: 'التالي',
      search: 'بحث',
      cancel: 'إلغاء',
      confirm: 'تأكيد',
      close: 'إغلاق',
      select: 'اختيار',
      viewDetails: 'عرض التفاصيل',
      viewOrderList: 'عرض قائمة الطلبات',
      status: 'الحالة',
      apply: 'تطبيق',
      clear: 'مسح',
      filters: 'عوامل التصفية',
      exportExcel: 'تصدير Excel',
      edit: 'تعديل',
      previousPage: 'الصفحة السابقة',
      nextPage: 'الصفحة التالية',
    },
    columns: {
      sNo: 'م',
      voucherNo: 'رقم السند',
      clientBillNo: 'رقم فاتورة العميل',
      batchNo: 'رقم الدفعة',
      client: 'العميل',
      customer: 'الزبون',
      deliveryAddress: 'عنوان التسليم',
      orderDate: 'تاريخ الطلب',
      deliveryDate: 'تاريخ التسليم',
      product: 'المنتج',
      deliveryCh: 'رسوم التوصيل',
      total: 'الإجمالي',
      payType: 'نوع الدفع',
      action: 'إجراء',
      mobile: 'الجوال',
      emirate: 'الإمارة',
      area: 'المنطقة',
      refNo: 'المرجع',
      date: 'التاريخ',
      driver: 'السائق',
      vehicle: 'المركبة',
      remarks: 'ملاحظات',
      createdBy: 'أنشأه',
      employee: 'الموظف',
      clients: 'العملاء',
      totalOrders: 'إجمالي الطلبات',
      options: 'خيارات',
      option: 'خيار',
      amount: 'المبلغ',
      deliveryNote: 'مذكرة التسليم',
      expectedAmount: 'المبلغ المتوقع',
      totalExpense: 'إجمالي المصروف',
      cashInHand: 'النقد في اليد',
      totalIncome: 'إجمالي الإيراد',
      totalVat: 'إجمالي ض. الق.م',
      clientAddress: 'عنوان العميل',
      customerAddress: 'عنوان الزبون',
      productPrice: 'سعر المنتج',
      deliveryCharge: 'رسوم التوصيل',
      tax: 'الضريبة',
      customerAmount: 'مبلغ الزبون',
      clientAmount: 'مبلغ العميل',
      clientMob: 'جوال العميل',
      clientEmirate: 'إمارة العميل',
      clientLocation: 'موقع العميل',
      customerMob: 'جوال الزبون',
      customerEmirate: 'إمارة الزبون',
      customerLocation: 'موقع الزبون',
      clientBill: 'فاتورة العميل',
      totalAdvance: 'إجمالي السلفة',
      balanceIncome: 'رصيد الإيراد',
      totalMoneyFromClient: 'إجمالي المال من العميل',
      totalClientMoney: 'إجمالي مال العميل',
      createdDate: 'تاريخ الإنشاء',
    },
    orders: {
      orderList: {
        keyword: 'كلمة البحث',
        keywordPlaceholder: 'عميل / زبون / رقم فاتورة / جوال',
        voucherPlaceholder: 'رقم السند',
        noOrders: 'لا توجد طلبات.',
        showingToOf: 'عرض {from} إلى {to} من أصل {total} طلباً',
        pageOf: 'صفحة {page} من {totalPages}',
      },
      todaysOrders: {
        statTotalOrders: 'إجمالي الطلبات',
        statDelivered: 'تم التسليم',
        statPending: 'معلق',
        statInTransit: 'قيد النقل',
        statReturn: 'مرتجع',
        statCancelled: 'ملغى',
        byEmirate: 'حسب الإمارة',
        byArea: 'حسب المنطقة',
        byStatus: 'حسب الحالة',
        noData: 'لا توجد بيانات',
        orderListHeading: 'قائمة الطلبات',
        noOrdersToday: 'لا توجد طلبات اليوم.',
        showingToOf: 'عرض {from} إلى {to} من أصل {total}',
        pageOf: 'صفحة {page} من {totalPages}',
      },
      createOrder: {
        statusPrefix: 'الحالة:',
        voucherNo: 'رقم السند',
        refNo: 'رقم المرجع',
        pickUpBy: 'الاستلام بواسطة',
        payType: 'نوع الدفع',
        chargeFrom: 'التحصيل من',
        clientBillNo: 'رقم فاتورة العميل',
        type: 'النوع',
        agent: 'الوكيل',
        clearSelection: 'مسح الاختيار',
        searchSelectDriver: 'ابحث أو اختر السائق',
        paymentTypeDetails: 'تفاصيل أنواع الدفع',
        selectType: 'اختر النوع',
        selectAgent: 'اختر الوكيل',
        chargeFromClient: 'العميل',
        chargeFromArea: 'المنطقة',
        forPickup: 'للاستلام؟',
        exchange: 'تبديل؟',
        fragile: 'قابل للكسر؟',
        deliveringArea: 'منطقة التسليم:',
        sectionClient: 'العميل',
        sectionCustomer: 'الزبون',
        clientName: 'اسم العميل',
        customerName: 'اسم الزبون',
        clientNo: 'رقم العميل',
        mobileNo: 'رقم الجوال',
        mobileNo1: 'جوال 1',
        mobileNo2: 'جوال 2',
        emirate: 'الإمارة',
        location: 'الموقع',
        locationUrl: 'رابط الموقع',
        address: 'العنوان',
        poBox: 'صندوق البريد',
        phSearchNewClient: 'ابحث أو أدخل عميلاً جديداً',
        phTypeClientNumber: 'أدخل رقم العميل',
        phTypeMobile: 'أدخل رقم الجوال',
        phSearchNewCustomer: 'ابحث أو أدخل زبوناً جديداً',
        phOptional: 'اختياري',
        selectEmirate: 'اختر الإمارة',
        phSearchLocation: 'ابحث أو اختر الموقع',
        phUrl: 'رابط',
        phEnterAddress: 'أدخل العنوان',
        phPoBox: 'ص.ب',
        phRemarks: 'أدخل الملاحظات',
        phAmount: '0.00',
        refreshClient: 'تحديث العميل',
        refreshAddress: 'تحديث العنوان',
        searchAddress: 'بحث في العنوان',
        refreshCustomer: 'تحديث الزبون',
        editCustomer: 'تعديل الزبون',
        orderDetailsFinancial: 'تفاصيل الطلب والمالية',
        remarks: 'ملاحظات',
        quantity: 'الكمية',
        pickupDate: 'تاريخ الاستلام',
        deliveryDate: 'تاريخ التسليم',
        productPrice: 'سعر المنتج',
        deliveryCharge: 'رسوم التوصيل',
        tax: 'الضريبة',
        clientAmount: 'مبلغ العميل',
        customerAmount: 'مبلغ الزبون',
        confirmDeleteTitle: 'تأكيد الحذف',
        confirmDeleteBody: 'اضغط تأكيد لحذف هذا الطلب.',
        clientAddressTitle: 'عناوين العميل',
        customerAddressTitle: 'عناوين الزبون',
        addressTablePoBox: 'ص.ب',
        paymentTableType: 'النوع',
        paymentTableRemarks: 'الملاحظات',
        payCad: 'رسوم التوصيل بعد التسليم',
        payCod: 'رسوم التوصيل عند التسليم',
        payCao: 'دفعة مقدمة لرسوم التوصيل نقداً في المكتب',
        payTao: 'دفعة مقدمة لرسوم التوصيل بالتحويل الإلكتروني',
        payFoc: 'توصيل بدون رسوم',
        payAps: 'سعر المنتج مدفوع مقدماً، العميل يدفع رسوم التوصيل',
        payApr: 'سعر المنتج مدفوع مقدماً، الزبون يدفع رسوم التوصيل',
      },
    },
    delivery: {
      notes: {
        subtitle: 'إدارة وعرض جميع مذكرات التسليم',
        listTitle: 'قائمة مذكرات التسليم',
        totalCount: 'الإجمالي: {total} مذكرة',
        searchRefLabel: 'بحث (رقم المرجع)',
        searchRefPlaceholder: 'البحث برقم المرجع...',
        allDrivers: 'كل السائقين',
        allStatus: 'كل الحالات',
        statusConfirmed: 'مؤكد',
        statusNew: 'جديد',
        statusFinished: 'منتهي',
        loading: 'جاري تحميل مذكرات التسليم...',
        noNotes: 'لا توجد مذكرات تسليم',
        noNotesHint: 'جرّب تعديل عوامل التصفية أو حدّث الصفحة',
        ordersInNote: 'الطلبات ({count})',
        detailsTitle: 'تفاصيل مذكرة التسليم',
        showingToOf: 'عرض {from} إلى {to} من {total} مذكرة',
        confirmDeleteTitle: 'تأكيد الحذف',
        confirmDeleteMessage: 'هل تريد بالتأكيد حذف مذكرة التسليم «{refNo}»؟',
        confirmDeleteWarning:
          'لا يمكن التراجع عن هذا الإجراء. سيتم حذف جميع البيانات المرتبطة نهائياً.',
        deleting: 'جاري الحذف…',
        printError: 'خطأ في الطباعة',
      },
    },
    shipments: {
      list: {
        subtitle: 'عرض وإدارة جميع الشحنات',
        searchPlaceholder: 'البحث برقم المرجع...',
        typeLabel: 'النوع',
        typeAll: 'الكل',
        typeNew: 'جديد (غير معتمد)',
        loading: 'جاري تحميل الشحنات...',
        loadingMore: 'جاري تحميل المزيد من الشحنات...',
        noShipments: 'لا توجد شحنات',
        showingToOf: 'عرض {from} إلى {to} من {total} شحنة',
        showingLoadedOf: 'عرض {loaded} من {total} شحنة',
        scrollToLoadMore: '(مرّر للأسفل لتحميل المزيد)',
        loadFailed: 'تعذّر تحميل الشحنات',
        errorGeneric: 'حدث خطأ ما',
        noDataExport: 'لا توجد بيانات للتصدير',
        exportSuccess: 'تم تصدير ملف Excel بنجاح',
        exportFailed: 'تعذّر تصدير ملف Excel',
        printFailed: 'تعذّر طباعة الشحنة',
        deleteSuccess: 'تم حذف الشحنة بنجاح',
        deleteFailed: 'تعذّر حذف الشحنة',
        confirmDeleteTitle: 'تأكيد الحذف',
        confirmDeleteMessage: 'هل تريد بالتأكيد حذف الشحنة «{refNo}»؟',
        confirmDeleteWarning: 'يمكن حذف الشحنات غير المعتمدة فقط. ستعود مذكرة التسليم إلى حالة مؤكدة.',
        deleting: 'جاري الحذف...',
        statusApproved: 'معتمد',
        statusPending: 'قيد الانتظار',
        sheetName: 'الشحنات',
      },
    },
  },
};

/**
 * @param {string} lang 'en' | 'ar'
 * @returns {(path: string, vars?: Record<string, string | number>) => string}
 */
export function getAppUi(lang) {
  const root = UI[lang] || UI.en;
  return (path, vars) => {
    let s = resolveDot(root, path);
    if (typeof s !== 'string') s = path;
    if (vars && typeof s === 'string') {
      return s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
    }
    return s;
  };
}
